import { IconButton } from "$components/button/icon-button";
import { makeStyles } from "$components/mui-styles";
import TextField from "$components/text-field";
import {
  useConvertFromPenceFactory,
  useConvertFromPoundsFactory,
} from "$routes/_lib/use-convert-currency";
import useCurrencyFormat from "$routes/_lib/use-currency-format";
import { Grid, InputAdornment, Typography } from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { path } from "ramda";
import { useEffect } from "react";
import { type FieldErrors, useFormContext, useWatch } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";

import { BALANCE, INSURERS, TOTAL } from "./formConstants";
import type { InsurerSummaryProps } from "./types";

const useStyles = makeStyles((theme) => ({
  expandIcon: {
    transform: ({ expanded }: { expanded: boolean }) =>
      expanded ? "rotate(0deg)" : "rotate(180deg)",
    transition: "transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
  },
  cost: {
    height: "60px",
  },
  currency: {
    marginTop: theme.spacing(3),
  },
  label: {
    color: theme.palette.grey[600],
    [theme.breakpoints.down("xs")]: {
      fontSize: "10px",
    },
  },
  disabled: {
    color: theme.palette.text.primary,
  },
}));

export const InsurerSummary = ({
  index,
  expanded,
  setExpanded,
  insuranceName,
}: InsurerSummaryProps) => {
  const classes = useStyles({ expanded });
  const { formatMessage } = useIntl();
  const currency = useCurrencyFormat();
  const penceToPounds = useConvertFromPenceFactory();
  const poundsToPence = useConvertFromPoundsFactory();
  const { setValue, register, formState } = useFormContext();
  const costFormFieldPath = `${INSURERS}.${index}.${BALANCE}.${TOTAL}`;

  useEffect(() => {
    register(costFormFieldPath);
  }, [register, costFormFieldPath]);

  const expandButtonLabel = expanded
    ? formatMessage({
        id: "insuranceAccordion.hide-insurance-details",
        defaultMessage: "Hide Insurance Details",
      })
    : formatMessage({
        id: "insuranceAccordion.show-insurance-details",
        defaultMessage: "Show Insurance Details",
      });

  const costLabel = formatMessage({
    id: "insuranceAccordion.summary.amount",
    defaultMessage: "Price",
  });

  const watchedCost = useWatch({ name: costFormFieldPath });
  const formattedCurrency = currency?.formatToParts(Number(currency))[0].value;

  return (
    <Grid container justifyContent="space-between" alignItems="center">
      <Grid item xs={8}>
        <Typography variant="h6">
          <FormattedMessage
            id="insuranceAccordion.summary.insuranceName"
            defaultMessage="{insuranceName}"
            values={{ insuranceName }}
          />
        </Typography>
      </Grid>
      <Grid item xs className={classes.cost}>
        <TextField
          name={costFormFieldPath}
          label={costLabel}
          type="number"
          InputLabelProps={{ classes: { root: classes.label } }}
          error={path<FieldErrors>(
            costFormFieldPath.split("."),
            formState.errors,
          )?.message?.message?.toString()}
          InputProps={{
            value: penceToPounds(watchedCost),
            inputProps: { min: "0.00", step: "0.01" },
            onChange: (e) => {
              setValue(costFormFieldPath, poundsToPence(e.target.value), {
                shouldDirty: true,
                shouldValidate: true,
                shouldTouch: true,
              });
            },
            classes: { disabled: classes.disabled },
            startAdornment: formattedCurrency && (
              <InputAdornment position="start">
                <p className={classes.currency}>{formattedCurrency || ""}</p>
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      <Grid item>
        <IconButton
          onClick={() => setExpanded(!expanded)}
          aria-label={expandButtonLabel}
        >
          <ExpandMoreIcon className={classes.expandIcon} />
        </IconButton>
      </Grid>
    </Grid>
  );
};

export default InsurerSummary;
